export { COOKIE_NAME, ONE_YEAR_MS } from "@shared/const";
import { getAttributionPayload, getStoredGclid } from "@/lib/attribution";

export const APP_TITLE = import.meta.env.VITE_APP_TITLE || "App";

export const APP_LOGO =
  import.meta.env.VITE_APP_LOGO ||
  "https://placehold.co/128x128/E1E7EF/1F2937?text=App";

export const DISCORD_INVITE_URL = "https://discord.com/invite/Vn6RKD8WrR";

const getCurrentPath = () =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

const resolveRedirectTarget = (redirectTo?: string) => {
  const target = redirectTo || getCurrentPath();
  if (
    target.startsWith("com.aisongmaker.app:/") ||
    target.startsWith("com.solmiai.app:/") ||
    target.startsWith("aisongmaker:/")
  ) {
    return target;
  }

  const url = new URL(target, window.location.origin);
  if (url.origin !== window.location.origin) {
    return `${window.location.origin}${getCurrentPath()}`;
  }
  return url.toString();
};

// URL-safe base64 encode of the OAuth state payload (redirect + attribution +
// gclid). Standard base64 ("+", "/", "=") gets mangled in the OAuth redirect
// round-trip ("+" decodes to a space in query strings), which made
// decodeOAuthState fail and dropped attribution + gclid on every signup once
// state switched from a plain redirect URL to encoded JSON. encodeURIComponent
// first so any non-ASCII attribution value stays UTF-8 safe (btoa alone throws
// on those, which would break the whole login).
const encodeOAuthState = (redirectTo?: string) => {
  const stateTarget = resolveRedirectTarget(redirectTo);
  const attribution = getAttributionPayload();
  const gclid = getStoredGclid();
  const json = JSON.stringify({
    redirect: stateTarget,
    attribution,
    ...(gclid && { gclid }),
  });
  const utf8 = encodeURIComponent(json).replace(/%([0-9A-F]{2})/g, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16)),
  );
  return btoa(utf8)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

// Generate login URL at runtime so redirect URI reflects the current origin.
export const getLoginUrl = (redirectTo?: string) => {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (!clientId) {
    throw new Error("Missing VITE_GOOGLE_CLIENT_ID env variable");
  }

  const redirectUri = `${window.location.origin}/api/oauth/callback`;
  const state = encodeOAuthState(redirectTo);

  const url = new URL("https://accounts.google.com/o/oauth2/v2/auth");
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("scope", "openid email profile");
  url.searchParams.set("include_granted_scopes", "true");
  url.searchParams.set("state", state);
  url.searchParams.set("prompt", "select_account");

  return url.toString();
};

// Web "Sign in with Apple". Requires an Apple **Services ID** (not the native
// app bundle ID) as the client_id, exposed to the client as
// VITE_APPLE_WEB_CLIENT_ID. Apple returns name/email only when the scope is
// requested, which forces response_mode=form_post — so Apple POSTs (not GETs)
// the authorization back to /api/oauth/apple/callback, where the server
// exchanges the code with a signed client secret. The redirect URI must be
// HTTPS and registered exactly in the Apple Services ID config (localhost is
// not accepted by Apple, so this flow only works on deployed origins).
export const APPLE_WEB_CLIENT_ID = import.meta.env
  .VITE_APPLE_WEB_CLIENT_ID as string | undefined;

export const isAppleSignInConfigured = () =>
  typeof APPLE_WEB_CLIENT_ID === "string" && APPLE_WEB_CLIENT_ID.length > 0;

export const getAppleLoginUrl = (redirectTo?: string) => {
  const clientId = APPLE_WEB_CLIENT_ID;
  if (!clientId) {
    throw new Error("Missing VITE_APPLE_WEB_CLIENT_ID env variable");
  }

  const redirectUri = `${window.location.origin}/api/oauth/apple/callback`;
  const state = encodeOAuthState(redirectTo);

  const url = new URL("https://appleid.apple.com/auth/authorize");
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("response_type", "code");
  // form_post is required whenever name/email scopes are requested.
  url.searchParams.set("response_mode", "form_post");
  url.searchParams.set("scope", "name email");
  url.searchParams.set("state", state);

  return url.toString();
};
